import React, { useState } from 'react';
import ActionButton from '../../../Components/Common/ActionButton';
import { DetailCard, MetaList } from '../../../Components/Common/Detail';
import notify from '../../../utils/notify';
import { confirmAction } from '../../../utils/confirm';
import useAwardingApi from '../hooks/useAwardingApi';
import UploadDealRecapModal from './UploadDealRecapModal';
import PreviewDocumentModal from './PreviewDocumentModal';
import ResponseSection from './ResponseSection';
import { formatDateToDDMMYYYY } from '../../../utils/dateformatter';
import { AWARDING_SENDER, AWARDING_STATUS } from '../utils/awardingHelpers';
import { formatFileSize } from '../../../utils/format';

const DEAL_RECAP_DOCUMENT_TYPE = 1;

const AwardingTab = ({
  awardingId,
  awarding,
  responses = [],
  isLoadingResponses = false,
  onChanged,
  onRespondCreated,
  mode = 'evaluator',
}) => {
  const isSupplier = mode === 'supplier';
  const { uploadDocument, deleteDocument, createResponse } = useAwardingApi();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const documents = (awarding?.documents ?? []).filter(
    doc => doc.documentType === DEAL_RECAP_DOCUMENT_TYPE
  );
  const dealRecap = documents[0] ?? null;
  const status = awarding?.status ?? AWARDING_STATUS.open;
  const isSent = status !== AWARDING_STATUS.open;
  const isConfirmed = status === AWARDING_STATUS.confirmed;
  const canModifyDocument = !isConfirmed && !isSupplier;

  const toggleModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(prev => !prev);
  };

  const togglePreview = () => setIsPreviewOpen(prev => !prev);

  const handleSubmit = async ({
    file,
    companyMitraId,
    picName,
    tradingEmail,
  }) => {
    const confirmed = await confirmAction({
      tone: 'primary',
      title: 'Send deal recap to the supplier?',
      message:
        'The deal recap is sent to the selected registered supplier right away. A sent document cannot be recalled, only replaced or removed until the awarding is confirmed.',
      subject: file?.name,
      confirmLabel: 'Send deal recap',
    });
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      await uploadDocument(awardingId, file, {
        documentType: DEAL_RECAP_DOCUMENT_TYPE,
        companyMitraId,
        picName,
        tradingEmail,
      });
      notify.success('Deal recap sent to Registered Supplier successfully.');
      setIsModalOpen(false);
      await onChanged?.();
    } catch (error) {
      notify.apiError(error, 'send deal recap');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!dealRecap?.id) return;

    setIsDeleting(true);
    try {
      await deleteDocument(dealRecap.id);
      notify.success('Deal recap removed successfully.');
      await onChanged?.();
    } catch (error) {
      notify.apiError(error, 'remove deal recap');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRespond = async ({ message }) => {
    setIsResponding(true);
    try {
      await createResponse(awardingId, {
        message,
        senderType: isSupplier
          ? AWARDING_SENDER.companyMitra
          : AWARDING_SENDER.pertamina,
      });
      notify.success('Response submitted successfully.');
      await onRespondCreated?.();
      return true;
    } catch (error) {
      notify.apiError(error, 'submit response');
      return false;
    } finally {
      setIsResponding(false);
    }
  };

  const documentUpdatedAt =
    dealRecap?.createdDate && formatDateToDDMMYYYY(dealRecap.createdDate);

  const renderStartAwarding = () => (
    <DetailCard title='Start awarding' icon='ri-award-line'>
      <div className='text-center py-3'>
        <p className='text-muted fs-13 mb-4'>
          Upload the deal recap and send to Registered Supplier winner.
        </p>

        <button
          type='button'
          onClick={toggleModal}
          className='d-inline-flex align-items-center gap-3 border border-primary rounded p-3 bg-soft-primary text-start'
          style={{ minWidth: 'min(320px, 100%)', maxWidth: '100%' }}
        >
          <span className='avatar-sm d-flex align-items-center justify-content-center bg-white rounded'>
            <i className='ri-upload-2-line fs-20 text-primary'></i>
          </span>
          <span>
            <span className='d-block fw-medium text-dark'>Upload File</span>
            <small className='text-muted'>Browse from your device</small>
          </span>
        </button>
      </div>
    </DetailCard>
  );

  const renderSentAwarding = () => (
    <>
      <div className='row g-3'>
        <div className={isSupplier ? 'col-12' : 'col-12 col-lg-6'}>
          <DetailCard
            title='Deal recap'
            icon='ri-file-pdf-line'
            className='h-100 mb-0'
          >
            <div className='d-flex flex-wrap align-items-center gap-3'>
              <span className='avatar-sm d-flex align-items-center justify-content-center bg-light rounded flex-shrink-0'>
                <i className='ri-file-pdf-line fs-20 text-danger'></i>
              </span>
              <div className='flex-grow-1' style={{ minWidth: 0 }}>
                <span className='d-block fw-semibold text-dark'>
                  {dealRecap?.originalFileName ||
                    dealRecap?.fileName ||
                    'Deal Recap'}
                </span>
                <small className='text-muted'>
                  {documentUpdatedAt
                    ? `Updated: ${documentUpdatedAt}`
                    : dealRecap
                      ? formatFileSize(dealRecap.fileSize)
                      : 'No document'}
                </small>
              </div>
              <div className='d-flex flex-wrap align-items-center gap-2'>
                <ActionButton
                  variant='secondary'
                  icon='ri-eye-line'
                  onClick={togglePreview}
                  disabled={!dealRecap}
                >
                  View
                </ActionButton>
                {canModifyDocument && (
                  <ActionButton
                    variant='secondary'
                    icon='ri-upload-2-line'
                    onClick={toggleModal}
                  >
                    {dealRecap ? 'Change Document' : 'Upload'}
                  </ActionButton>
                )}
                {canModifyDocument && dealRecap && (
                  <ActionButton
                    variant='icon'
                    danger
                    size='sm'
                    icon='ri-delete-bin-line'
                    label='Remove file'
                    confirm={{
                      title: 'Remove deal recap?',
                      message:
                        'The uploaded deal recap document will be removed from this awarding. This cannot be undone.',
                      confirmLabel: 'Remove deal recap',
                    }}
                    onClick={handleRemove}
                    disabled={isDeleting}
                  />
                )}
              </div>
            </div>
          </DetailCard>
        </div>

        {!isSupplier && (
          <div className='col-12 col-lg-6'>
            <DetailCard
              title='Recipient'
              icon='ri-building-line'
              className='h-100 mb-0'
            >
              <MetaList
                items={[
                  {
                    label: 'Registered supplier',
                    value: awarding?.companyMitraName,
                  },
                  { label: 'PIC', value: awarding?.picName },
                  { label: 'Trading email', value: awarding?.tradingEmail },
                ]}
              />
            </DetailCard>
          </div>
        )}
      </div>

      <ResponseSection
        responses={responses}
        isLoading={isLoadingResponses}
        canRespond={!isConfirmed}
        isSubmitting={isResponding}
        onSubmit={handleRespond}
      />
    </>
  );

  return (
    <div>
      {isSent ? renderSentAwarding() : renderStartAwarding()}

      {!isSupplier && (
        <UploadDealRecapModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          defaultValues={{
            companyMitraId: awarding?.companyMitraId ?? '',
            picName: awarding?.picName ?? '',
            tradingEmail: awarding?.tradingEmail ?? '',
          }}
        />
      )}

      <PreviewDocumentModal
        isOpen={isPreviewOpen}
        toggle={togglePreview}
        document={dealRecap}
      />
    </div>
  );
};

export default AwardingTab;
